import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, Loader2, Server, Upload } from 'lucide-react';
import type { CobradorRecipeResult, PortfolioImportMapping, PortfolioImportResult } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { contactAgentApi } from '@/features/contact-agent/api/contactAgentApi';
import { contactAgentError, setContactAgentTenantId } from '@/features/contact-agent/api/contactAgentScope';
import { guessImportMapping, IMPORT_FIELD_GROUPS } from '@/features/contact-agent/api/importMapping';
import { ContactAgentSourceFields } from '@/features/contact-agent/components/ContactAgentSourceFields';
import { platformAdminApi } from '../api/platformAdminApi';
import { missingImportFields, defaultAgentName, type DebtSource } from './cobradorRecipe.utils';
import {
  ClientFields,
  Question,
  RecipeDone,
  RecipeFooter,
  RecipePage,
} from './recipeParts';
import { SELECT_CLASS, useActiveTenants } from './recipe.hooks';

type ExcelState = {
  file: File;
  columns: string[];
  rowCount: number;
  mapping: PortfolioImportMapping;
};

const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  IMPORT_FIELD_GROUPS.flatMap(group => group.fields.map(field => [field.key, field.label])),
);

export function CobradorRecipePage() {
  const [error, setError] = useState('');
  const tenants = useActiveTenants(setError);
  const [tenantId, setTenantId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [agentName, setAgentName] = useState('');
  const [source, setSource] = useState<DebtSource>('EXCEL');
  const [excel, setExcel] = useState<ExcelState | null>(null);
  const [reading, setReading] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [paymentMethodsText, setPaymentMethodsText] = useState('');
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<CobradorRecipeResult | null>(null);
  const [imported, setImported] = useState<PortfolioImportResult | null>(null);

  const readFile = async (file: File) => {
    setError('');
    setReading(true);
    try {
      const preview = await contactAgentApi.previewImportPortfolio(file);
      setExcel({
        file,
        columns: preview.columns,
        rowCount: preview.rowCount,
        mapping: guessImportMapping(preview.columns),
      });
    } catch (err) {
      setExcel(null);
      setError(contactAgentError(err));
    } finally {
      setReading(false);
    }
  };

  const missing = useMemo(() => (excel ? missingImportFields(excel.mapping) : []), [excel]);

  const answered = [
    Boolean(tenantId && companyName.trim()),
    source === 'EXCEL' ? Boolean(excel && missing.length === 0) : Boolean(connectionId),
    Boolean(paymentMethodsText.trim()),
  ];
  const ready = answered.every(Boolean);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || progress) return;
    setError('');
    try {
      let importResult: PortfolioImportResult | null = null;
      if (source === 'EXCEL' && excel) {
        setProgress(`Importando ${excel.rowCount} filas de cartera`);
        importResult = await contactAgentApi.importPortfolio(excel.file, excel.mapping);
        if (importResult.createdCount + importResult.updatedCount === 0) {
          throw new Error(
            importResult.errors[0] ?? 'El archivo no tenía filas válidas. Revisá teléfono, factura, monto y vencimiento.',
          );
        }
      }
      setProgress('Creando el Cobrador');
      const created = await platformAdminApi.createCobrador({
        tenantId,
        companyName: companyName.trim(),
        agentName: agentName.trim() || undefined,
        paymentMethodsText: paymentMethodsText.trim(),
        sourceConnectionId: source === 'SETUP' ? connectionId : null,
      });
      setImported(importResult);
      setResult(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el Cobrador');
    } finally {
      setProgress('');
    }
  };

  if (result) {
    return (
      <RecipeDone
        title={`El Cobrador de ${companyName} está creado`}
        subtitle='Falta un solo paso para que empiece a cobrar: conectarle un número de WhatsApp.'
        steps={result.steps}
        aiReady={result.aiReady}
        note={
          imported
            ? `Cartera: ${imported.createdCount} deudas nuevas, ${imported.updatedCount} actualizadas${
                imported.errorCount > 0 ? `, ${imported.errorCount} filas con errores` : ''
              }.`
            : undefined
        }
        actions={
          <>
            <Button asChild size='lg'>
              <Link to='/admin/whatsapp'>Conectar WhatsApp</Link>
            </Button>
            <Button asChild size='lg' variant='outline'>
              <Link to={`/admin/contact-agent/agents/${result.agentId}`}>Ver el Cobrador</Link>
            </Button>
          </>
        }
      />
    );
  }

  return (
    <RecipePage
      title='Nuevo Cobrador'
      intro='Tres respuestas y el agente queda listo para cobrar. Todo lo demás viene configurado y se puede ajustar después en Cobranzas.'
    >
      <form onSubmit={handleSubmit} noValidate>
        <ol className='relative'>
          <Question done={answered[0]} title='¿Para qué cliente?' hint='El Cobrador queda dentro de su cuenta.'>
            <ClientFields
              tenants={tenants}
              tenantId={tenantId}
              onTenant={tenant => {
                setTenantId(tenant.id);
                setContactAgentTenantId(tenant.id);
                setConnectionId(null);
                setExcel(null);
                setCompanyName(tenant.name);
              }}
              companyName={companyName}
              onCompanyName={setCompanyName}
              agentName={agentName}
              onAgentName={setAgentName}
              defaultAgentName={defaultAgentName(companyName)}
            />
          </Question>

          <Question
            done={answered[1]}
            title='¿De dónde sale la deuda?'
            hint='Un Excel con las facturas pendientes, o la base de Setup del cliente.'
          >
            <div role='radiogroup' aria-label='Origen de la cartera' className='grid grid-cols-2 gap-2'>
              <SourceOption
                selected={source === 'EXCEL'}
                icon={<FileSpreadsheet className='h-5 w-5' aria-hidden />}
                title='Excel o CSV'
                onSelect={() => setSource('EXCEL')}
              />
              <SourceOption
                selected={source === 'SETUP'}
                icon={<Server className='h-5 w-5' aria-hidden />}
                title='Setup'
                onSelect={() => setSource('SETUP')}
              />
            </div>

            <div className='mt-4'>
              {source === 'EXCEL' ? (
                <ExcelPicker
                  disabled={!tenantId}
                  reading={reading}
                  excel={excel}
                  missing={missing}
                  onFile={file => void readFile(file)}
                  onMap={(key, column) =>
                    setExcel(current =>
                      current ? { ...current, mapping: { ...current.mapping, [key]: column } } : current,
                    )
                  }
                />
              ) : tenantId ? (
                <ContactAgentSourceFields
                  key={tenantId}
                  tenantId={tenantId}
                  sourceConnectionId={connectionId}
                  setupOnly
                  onBound={id => setConnectionId(id)}
                />
              ) : (
                <p className='text-sm text-muted-foreground'>Elegí primero el cliente.</p>
              )}
            </div>
          </Question>

          <Question
            done={answered[2]}
            last
            title='¿Cómo le pagan?'
            hint='El agente le pasa esto al deudor cuando pregunta cómo pagar.'
          >
            <Label htmlFor='payments' className='sr-only'>
              Medios de pago
            </Label>
            <Textarea
              id='payments'
              rows={4}
              maxLength={2000}
              value={paymentMethodsText}
              placeholder={'Transferencia: CBU 0000000000000000000000\nAlias: empresa.cobranzas\nMercado Pago: link de pago'}
              onChange={event => setPaymentMethodsText(event.target.value)}
            />
          </Question>
        </ol>

        <RecipeFooter error={error} progress={progress} answered={answered} submitLabel='Crear Cobrador' />
      </form>
    </RecipePage>
  );
}

function SourceOption(props: { selected: boolean; icon: ReactNode; title: string; onSelect: () => void }) {
  return (
    <button
      type='button'
      role='radio'
      aria-checked={props.selected}
      onClick={props.onSelect}
      className={cn(
        'flex items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        props.selected
          ? 'border-primary bg-primary/5 text-foreground'
          : 'border-input text-muted-foreground hover:border-foreground/30 hover:text-foreground',
      )}
    >
      {props.icon}
      {props.title}
    </button>
  );
}

function ExcelPicker(props: {
  disabled: boolean;
  reading: boolean;
  excel: ExcelState | null;
  missing: Array<keyof PortfolioImportMapping>;
  onFile: (file: File) => void;
  onMap: (key: keyof PortfolioImportMapping, column: string) => void;
}) {
  const { excel, missing } = props;
  return (
    <div className='space-y-4'>
      <label
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-input px-4 py-4 text-sm transition-colors hover:border-foreground/30 focus-within:ring-2 focus-within:ring-ring',
          props.disabled && 'pointer-events-none opacity-50',
        )}
      >
        {props.reading ? (
          <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' aria-hidden />
        ) : (
          <Upload className='h-5 w-5 text-muted-foreground' aria-hidden />
        )}
        <span className='min-w-0 flex-1'>
          {excel ? (
            <>
              <span className='block truncate font-medium text-foreground'>{excel.file.name}</span>
              <span className='text-muted-foreground'>{excel.rowCount} filas · elegir otro archivo</span>
            </>
          ) : (
            <span className='text-muted-foreground'>
              {props.disabled ? 'Elegí primero el cliente' : 'Subí el Excel o CSV con las facturas pendientes'}
            </span>
          )}
        </span>
        <input
          type='file'
          accept='.xlsx,.xls,.csv'
          className='sr-only'
          disabled={props.disabled}
          onChange={event => {
            const file = event.target.files?.[0];
            if (file) props.onFile(file);
            event.target.value = '';
          }}
        />
      </label>

      {excel && missing.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          Columnas reconocidas: {describeMapping(excel.mapping)}.
        </p>
      ) : null}

      {excel && missing.length > 0 ? (
        <div className='space-y-3 rounded-lg bg-muted/50 p-4'>
          <p className='text-sm text-foreground'>
            No encontramos {missing.length === 1 ? 'esta columna' : 'estas columnas'}. Indicá cuál es en tu archivo:
          </p>
          {missing.map(key => (
            <div key={key} className='grid items-center gap-2 sm:grid-cols-[10rem_1fr]'>
              <Label htmlFor={`map-${key}`}>{FIELD_LABELS[key] ?? key}</Label>
              <select
                id={`map-${key}`}
                className={SELECT_CLASS}
                value={excel.mapping[key] ?? ''}
                onChange={event => props.onMap(key, event.target.value)}
              >
                <option value=''>Elegí una columna</option>
                {excel.columns.map(column => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function describeMapping(mapping: PortfolioImportMapping): string {
  const parts: string[] = [];
  for (const key of ['phone', 'email', 'invoiceNumber', 'amount', 'dueDate'] as const) {
    const column = mapping[key]?.trim();
    if (column) parts.push(`${FIELD_LABELS[key] ?? key} en «${column}»`);
  }
  return parts.join(', ');
}
